/**
 * Calculate cumulative P&L history from fills data
 * Since the backend doesn't store historical P&L snapshots,
 * we reconstruct it from fill (trade) history.
 */

import { useMemo } from "react";
import type { Fill, FundingPayment } from "@/backend/types";
import { parseDecimal } from "@/backend/types";

export type PnlDataPoint = {
  date: string;
  timestamp: number;
  pnl: number;
  cumulativePnl: number;
  fees: number;
  cumulativeFees: number;
  netPnl: number;
  cumulativeNetPnl: number;
};

type UsePnlHistoryProps = {
  fills: Fill[];
  fundingPayments: FundingPayment[];
};

export const usePnlHistory = ({ fills, fundingPayments }: UsePnlHistoryProps) => {
  const pnlHistory = useMemo(() => {
    if (!fills.length && !fundingPayments.length) {
      return [];
    }

    // Combine fills and funding payments into a single timeline
    type Event = {
      timestamp: number;
      date: string;
      pnl: number;
      fee: number;
    };

    const events: Event[] = [];

    // Add fills
    fills.forEach((fill) => {
      const timestamp = new Date(fill.created_at).getTime();
      events.push({
        timestamp,
        date: new Date(fill.created_at).toISOString().split("T")[0],
        pnl: parseDecimal(fill.realized_pnl),
        fee: parseDecimal(fill.fee),
      });
    });

    // Add funding payments
    fundingPayments.forEach((payment) => {
      const timestamp = new Date(payment.created_at).getTime();
      events.push({
        timestamp,
        date: new Date(payment.created_at).toISOString().split("T")[0],
        pnl: parseDecimal(payment.payment), // Positive = received, negative = paid
        fee: 0,
      });
    });

    // Sort by timestamp
    events.sort((a, b) => a.timestamp - b.timestamp);

    // Group by day and calculate cumulative values
    const dailyMap = new Map<string, { pnl: number; fees: number }>();

    events.forEach((event) => {
      const existing = dailyMap.get(event.date) || { pnl: 0, fees: 0 };
      dailyMap.set(event.date, {
        pnl: existing.pnl + event.pnl,
        fees: existing.fees + event.fee,
      });
    });

    // Convert to array and calculate cumulative values
    const sortedDates = Array.from(dailyMap.keys()).sort();
    let cumulativePnl = 0;
    let cumulativeFees = 0;
    let cumulativeNetPnl = 0;

    const history: PnlDataPoint[] = sortedDates.map((date) => {
      const daily = dailyMap.get(date)!;
      cumulativePnl += daily.pnl;
      cumulativeFees += daily.fees;
      const netPnl = daily.pnl - daily.fees;
      cumulativeNetPnl += netPnl;

      return {
        date,
        timestamp: new Date(date).getTime(),
        pnl: daily.pnl,
        cumulativePnl,
        fees: daily.fees,
        cumulativeFees,
        netPnl,
        cumulativeNetPnl,
      };
    });

    return history;
  }, [fills, fundingPayments]);

  // Summary stats
  const summary = useMemo(() => {
    if (!pnlHistory.length) {
      return {
        totalPnl: 0,
        totalFees: 0,
        totalNetPnl: 0,
        lastPnl: 0,
        change24h: 0,
        change24hPercent: 0,
      };
    }

    const last = pnlHistory[pnlHistory.length - 1];
    const totalPnl = last.cumulativePnl;
    const totalFees = last.cumulativeFees;
    const totalNetPnl = last.cumulativeNetPnl;

    // Calculate 24h change
    const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
    const recentPoints = pnlHistory.filter((p) => p.timestamp > oneDayAgo);
    const change24h = recentPoints.reduce((sum, p) => sum + p.netPnl, 0);
    const previousNetPnl = totalNetPnl - change24h;
    const change24hPercent =
      previousNetPnl !== 0 ? (change24h / Math.abs(previousNetPnl)) * 100 : 0;

    return {
      totalPnl,
      totalFees,
      totalNetPnl,
      lastPnl: last.netPnl,
      change24h,
      change24hPercent,
    };
  }, [pnlHistory]);

  return {
    pnlHistory,
    summary,
  };
};
